import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Spin, message, Empty, Card, Tag } from "antd";
import dayjs from "dayjs";
import axiosInstance from "api/axiosInstance";
import DashboardLayout from "../LayoutNavbar/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../LayoutNavbar/Navbars/DashboardNavbar";
import {
  STATUS_CONFIG,
} from "../../../Constants/Enums/Attendance/AttendanceStatusEnum";
import { CalendarViewTypeEnum } from "Constants/Enums/Attendance/CalendarViewTypeEnum";
import API from "api/endpoints";

const EmployeeCalendar = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [hoveredDate, setHoveredDate] = useState(null);
  const [viewType, setViewType] = useState("month");
  const navigate = useNavigate();

  const userId = localStorage.getItem("personalId");

  // Helper function to check if a date is in the future
  const isFutureDate = (date) => {
    return dayjs(date).isAfter(dayjs(), "day");
  };

  useEffect(() => {
    if (userId) fetchAttendance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, dayjs(currentDate).format('YYYY-MM-DD'), viewType]);

  const fetchAttendance = async () => {
    try {
      setLoading(true);

      let startDate, endDate;

      if (viewType === "month") {
        startDate = dayjs(currentDate).startOf("month").format("YYYY-MM-DD");
        endDate = dayjs(currentDate).endOf("month").format("YYYY-MM-DD");
      } else if (viewType === "week") {
        startDate = dayjs(currentDate).startOf("week").format("YYYY-MM-DD");
        endDate = dayjs(currentDate).endOf("week").format("YYYY-MM-DD");
      } else if (viewType === "day") {
        // Point 13: day view now uses the real Day enum + a single-day range.
        startDate = dayjs(currentDate).format("YYYY-MM-DD");
        endDate = dayjs(currentDate).format("YYYY-MM-DD");
      } else {
        startDate = dayjs(currentDate).format("YYYY-MM-DD");
        endDate = dayjs(currentDate).format("YYYY-MM-DD");
      }

      // Point 13: Day is now a real view type (CalendarViewTypeEnum.Day = 2).
      const apiViewType =
        viewType === "month"
          ? CalendarViewTypeEnum.Month
          : viewType === "day"
          ? CalendarViewTypeEnum.Day
          : CalendarViewTypeEnum.Week;

      const response = await axiosInstance.get(
        API.attendance.userAttendance(
          userId,
          apiViewType,
          startDate,
          endDate
        )
      );

      setRecords(response.data?.records || []);
    } catch (error) {
      console.error("Error fetching attendance:", error);
      if (error.response?.status === 401) {
        message.error("You are not authorized to view this data");
      } else if (error.response?.status === 404) {
        message.error("Attendance data not found");
      } else {
        message.error("Failed to load attendance data");
      }
    } finally {
      setLoading(false);
    }
  };

  // Format time function
  const formatTime = (timeString) => {
    if (!timeString || timeString === "00:00:00") return "";

    const parts = timeString.split(":");
    const hours = parseInt(parts[0]);
    const minutes = parseInt(parts[1]);

    if (hours === 0) return `${minutes}m`;
    if (minutes === 0) return `${hours}h`;

    return `${hours}h ${minutes}m`;
  };

  // Get attendance record for a specific date
  const getAttendanceRecord = (date) => {
    const dateString = dayjs(date).format("YYYY-MM-DD");
    return records.find((record) => record.date === dateString);
  };

  // Handle date click - navigate to day details page
  const handleDateClick = (date, record) => {
    if (isFutureDate(date)) {
      message.info("Cannot view future dates");
      return;
    }

    const formattedDate = dayjs(date).format("YYYY-MM-DD");

    navigate("/employeedashboard/attendance/daydetail", {
      state: {
        date: formattedDate,
        attendanceId: record?.id,
      },
    });
  };

  // Navigation functions
  const goToPrevious = () => {
    setCurrentDate((prev) => {
      const date = dayjs(prev);
      if (viewType === "month") {
        return date.subtract(1, "month").toDate();
      } else if (viewType === "week") {
        return date.subtract(1, "week").toDate();
      } else {
        return date.subtract(1, "day").toDate();
      }
    });
  };

  const goToNext = () => {
    setCurrentDate((prev) => {
      const date = dayjs(prev);
      if (viewType === "month") {
        return date.add(1, "month").toDate();
      } else if (viewType === "week") {
        return date.add(1, "week").toDate();
      } else {
        return date.add(1, "day").toDate();
      }
    });
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const handleViewChange = (newViewType) => {
    setViewType(newViewType);
    setCurrentDate(new Date());
  };

  // Generate calendar days based on view type
  const generateCalendarDays = () => {
    if (viewType === "month") {
      return generateMonthDays();
    } else if (viewType === "week") {
      return generateWeekDays();
    } else {
      return generateDayDays();
    }
  };

  const generateMonthDays = () => {
    const firstDay = dayjs(currentDate).startOf("month");
    const lastDay = dayjs(currentDate).endOf("month");
    const firstDayOfWeek = firstDay.day();

    const days = [];

    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      const date = firstDay.subtract(i + 1, "day");
      days.push({ date: date.toDate(), isOtherMonth: true });
    }

    for (let i = 0; i < lastDay.date(); i++) {
      const date = firstDay.add(i, "day");
      days.push({ date: date.toDate(), isOtherMonth: false });
    }

    const totalCells = 42;
    const nextMonthDays = totalCells - days.length;

    for (let i = 1; i <= nextMonthDays; i++) {
      const date = lastDay.add(i, "day");
      days.push({ date: date.toDate(), isOtherMonth: true });
    }

    return days;
  };

  const generateWeekDays = () => {
    const startOfWeek = dayjs(currentDate).startOf("week");
    const days = [];

    for (let i = 0; i < 7; i++) {
      const date = startOfWeek.add(i, "day");
      days.push({ date: date.toDate(), isOtherMonth: false });
    }

    return days;
  };

  const generateDayDays = () => {
    return [{ date: currentDate, isOtherMonth: false }];
  };

  const calendarDays = generateCalendarDays();
  const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];

  const getDisplayTitle = () => {
    if (viewType === "month") {
      return `${monthNames[dayjs(currentDate).month()]} ${dayjs(currentDate).year()}`;
    } else if (viewType === "week") {
      const startOfWeek = dayjs(currentDate).startOf("week");
      const endOfWeek = dayjs(currentDate).endOf("week");

      if (startOfWeek.month() === endOfWeek.month()) {
        return `${monthNames[startOfWeek.month()]} ${startOfWeek.date()} - ${endOfWeek.date()}, ${startOfWeek.year()}`;
      } else if (startOfWeek.year() === endOfWeek.year()) {
        return `${monthNames[startOfWeek.month()]} ${startOfWeek.date()} - ${monthNames[endOfWeek.month()]} ${endOfWeek.date()}, ${startOfWeek.year()}`;
      } else {
        return `${monthNames[startOfWeek.month()]} ${startOfWeek.date()}, ${startOfWeek.year()} - ${monthNames[endOfWeek.month()]} ${endOfWeek.date()}, ${endOfWeek.year()}`;
      }
    } else {
      return dayjs(currentDate).format("dddd, MMMM D, YYYY");
    }
  };

  // Helper function to convert hex to rgb
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
      : "0, 0, 0";
  };

  // Render day content for day view
  const renderDayContent = (date) => {
    const record = getAttendanceRecord(date);
    const status = record ? STATUS_CONFIG[record.status] : null;
    
    return (
      <Card className="day-attendance-card" style={{ margin: '20px' }}>
        <div className="day-status" style={{ marginBottom: '16px' }}>
          {status ? (
            <Tag color={status.color} style={{ fontSize: '16px', padding: '4px 12px' }}>
              {status.name}
            </Tag>
          ) : (
            <Tag color="default" style={{ fontSize: '16px', padding: '4px 12px' }}>
              No Data
            </Tag>
          )}
        </div>
        
        {record ? (
          <div className="day-details">
            <div className="detail-item" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
              <span className="detail-label" style={{ fontWeight: 'bold' }}>First Check In:</span>
              <span className="detail-value">
                {record.firstCheckIn ? dayjs(record.firstCheckIn).format("HH:mm") : "N/A"}
              </span>
            </div>
            
            <div className="detail-item" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
              <span className="detail-label" style={{ fontWeight: 'bold' }}>Last Check Out:</span>
              <span className="detail-value">
                {record.lastCheckOut ? dayjs(record.lastCheckOut).format("HH:mm") : "N/A"}
              </span>
            </div>
            
            <div className="detail-item" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
              <span className="detail-label" style={{ fontWeight: 'bold' }}>Total Worked Hours:</span>
              <span className="detail-value">
                {formatTime(record.totalWorkedHours)}
              </span>
            </div>
            
            {record.overtimeHours && record.overtimeHours !== "00:00:00" && (
              <div className="detail-item" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                <span className="detail-label" style={{ fontWeight: 'bold' }}>Overtime:</span>
                <span className="detail-value">
                  {formatTime(record.overtimeHours)}
                </span>
              </div>
            )}
            
            {record.remarks && (
              <div className="detail-item" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                <span className="detail-label" style={{ fontWeight: 'bold' }}>Remarks:</span>
                <span className="detail-value">{record.remarks}</span>
              </div>
            )}
            
            {(record?.isLate || record?.isEarlyLeave) && (
              <div className="detail-item" style={{ marginTop: '16px' }}>
                {record.isLate && (
                  <Tag color="orange" style={{ marginRight: '8px' }}>
                    <i className="fas fa-clock"></i> Late Arrival
                  </Tag>
                )}
                {record.isEarlyLeave && (
                  <Tag color="red">
                    <i className="fas fa-running"></i> Early Leave
                  </Tag>
                )}
              </div>
            )}
          </div>
        ) : (
          <Empty description="No attendance data for this day" />
        )}
      </Card>
    );
  };

  // Render calendar grid based on view type
  const renderCalendarGrid = () => {
    if (viewType === "day") {
      return (
        <div className="day-view">
          <div className="day-view-header" style={{ textAlign: 'center', marginBottom: '20px' }}>
            <h3>{dayjs(currentDate).format("dddd, MMMM D, YYYY")}</h3>
          </div>
          <div className="day-view-content">
            {renderDayContent(currentDate)}
          </div>
        </div>
      );
    }
    
    // Month or Week view
    return (
      <div className={`calendar-grid ${viewType}-view`}>
        {calendarDays.map(({ date, isOtherMonth }) => {
          const record = getAttendanceRecord(date);
          const isToday = dayjs(date).isSame(dayjs(), "day");
          const isHovered = hoveredDate === date.toISOString();
          const status = record ? STATUS_CONFIG[record.status] : null;

          return (
            <div
              key={date.toISOString()}
              className={`calendar-day ${isOtherMonth ? "other-month" : ""} ${
                isToday ? "today" : ""
              } ${isFutureDate(date) ? "future-date" : ""}`}
              onMouseEnter={() =>
                !isFutureDate(date) && setHoveredDate(date.toISOString())
              }
              onMouseLeave={() => setHoveredDate(null)}
              onClick={() =>
                !isFutureDate(date) && handleDateClick(date, record)
              }
            >
              {isFutureDate(date) && (
                <div className="future-date-overlay">
                  <i className="fas fa-lock"></i>
                  <span>Future Date</span>
                </div>
              )}
              
              {/* Day Header */}
              <div className="day-header">
                <div className={`day-number ${isToday ? "today" : ""}`}>{date.getDate()}</div>
                {status && (
                  <div
                    className={`day-status ${status.class}`}
                    style={{
                      backgroundColor: `rgba(${hexToRgb(status.color)}, 0.12)`,
                      color: status.color,
                      border: `1px solid rgba(${hexToRgb(status.color)}, 0.2)`,
                    }}
                  >
                    {status.short}
                  </div>
                )}
              </div>

              {/* Day Content */}
              <div className="day-content">
                {record &&
                  record.totalWorkedHours &&
                  record.totalWorkedHours !== "00:00:00" && (
                    <div className="day-hours">
                      <i className="fas fa-clock"></i>
                      {formatTime(record.totalWorkedHours)}
                    </div>
                  )}

                {record && record.overtimeHours && record.overtimeHours !== "00:00:00" && (
                  <div className="day-overtime">
                    <i className="fas fa-plus-circle"></i>
                    {formatTime(record.overtimeHours)}
                  </div>
                )}

                {/* Indicators */}
                {(record?.isLate || record?.isEarlyLeave) && (
                  <div className="day-indicators">
                    {record.isLate && (
                      <div className="indicator indicator-late">
                        <i className="fas fa-clock"></i> Late
                      </div>
                    )}
                    {record.isEarlyLeave && (
                      <div className="indicator indicator-early">
                        <i className="fas fa-running"></i> Early
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Hover Details */}
              {isHovered && (
                <div className="day-details">
                  <h6>{dayjs(date).format("ddd, MMM D, YYYY")}</h6>
                  {status && (
                    <p>
                      <strong>Status:</strong> {status.name}
                    </p>
                  )}
                  {record &&
                    record.totalWorkedHours &&
                    record.totalWorkedHours !== "00:00:00" && (
                      <p>
                        <strong>Worked:</strong> {formatTime(record.totalWorkedHours)}
                      </p>
                    )}
                  {record && record.overtimeHours && record.overtimeHours !== "00:00:00" && (
                    <p>
                      <strong>Overtime:</strong> {formatTime(record.overtimeHours)}
                    </p>
                  )}
                  {record?.firstCheckIn && (
                    <p>
                      <strong>First In:</strong> {dayjs(record.firstCheckIn).format("HH:mm")}
                    </p>
                  )}
                  {record?.lastCheckOut && (
                    <p>
                      <strong>Last Out:</strong> {dayjs(record.lastCheckOut).format("HH:mm")}
                    </p>
                  )}
                  {record?.remarks && (
                    <p>
                      <strong>Remarks:</strong> {record.remarks}
                    </p>
                  )}
                  <div className="click-hint">Click for details →</div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      {/* Calendar Container */}
      <div className="calendar-container">
        {/* Calendar Header */}
        <div className="calendar-header">
          {/* Navigation */}
          <div className="calendar-nav">
            <button onClick={goToPrevious} className="nav-button">
              <i className="fas fa-chevron-left"></i>
            </button>
            <button onClick={goToToday} className="nav-button">
              Today
            </button>
            <button onClick={goToNext} className="nav-button">
              <i className="fas fa-chevron-right"></i>
            </button>
          </div>

          {/* Calendar Title */}
          <div className="calendar-title">{getDisplayTitle()}</div>

          {/* View Options */}
          <div className="calendar-view-options">
            <button
              onClick={() => handleViewChange("month")}
              className={`view-button ${viewType === "month" ? "active" : ""}`}
            >
              Month
            </button>
            <button
              onClick={() => handleViewChange("week")}
              className={`view-button ${viewType === "week" ? "active" : ""}`}
            >
              Week
            </button>
            <button
              onClick={() => handleViewChange("day")}
              className={`view-button ${viewType === "day" ? "active" : ""}`}
            >
              Day
            </button>
          </div>
        </div>

        {/* Week Days Header */}
        {(viewType === "month" || viewType === "week") && (
          <div className="calendar-weekdays">
            {weekDays.map((day) => (
              <div key={day} className="calendar-weekday">
                {day}
              </div>
            ))}
          </div>
        )}

        {/* Calendar Grid */}
        <Spin spinning={loading}>
          {renderCalendarGrid()}
        </Spin>

        {/* Status Legend */}
        <div className="status-legend">
          {Object.entries(STATUS_CONFIG).map(([status, config]) => (
            <div key={status} className="legend-item">
              <div className="legend-color" style={{ backgroundColor: config.color }} />
              <span>{config.name}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Add Font Awesome CSS */}
      <link
        rel="stylesheet"
        href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css"
      />
    </DashboardLayout>
  );
};

export default EmployeeCalendar;